import { memo, useRef, useEffect } from 'react';
import { Search, X, Menu, Locate } from 'lucide-react';
import { getDisplayRegion } from './MapHelpers';
import SourceLogo from './SourceLogo';

const SearchBar = memo(({
  searchQuery,
  setSearchQuery,
  searchFocused,
  setSearchFocused,
  visibleBusinesses = [],
  onSelectBusiness,
  onToggleSidebar,
  onLocateUser,
  hasActiveFilters,
  onResetFilters
}) => {
  // The onBlur close is deferred 200ms so a click on a dropdown row registers
  // before the dropdown unmounts. Track the timer so we can cancel it on
  // unmount (avoids setState-after-unmount) and on selection (avoids the
  // close racing the click handler).
  const blurTimeout = useRef(null);

  useEffect(() => () => {
    if (blurTimeout.current) clearTimeout(blurTimeout.current);
  }, []);

  return (
    <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[400] w-full max-w-lg px-4">
      <div className="bg-background rounded-full shadow-lg border-2 border-orange-200 dark:border-orange-900 flex items-center px-2 md:px-4 py-2 gap-1">
        {/* Menu Button - Mobile Only */}
        <button
          onClick={onToggleSidebar}
          className="md:hidden p-2 hover:bg-muted rounded-full flex-shrink-0"
          title="Open list"
        >
          <Menu className="w-5 h-5 text-foreground" />
        </button>

        <Search className="w-5 h-5 text-muted-foreground flex-shrink-0" />
        <input
          type="text"
          placeholder="Search in Area"
          aria-label="Search businesses in area"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => {
            blurTimeout.current = setTimeout(() => setSearchFocused(false), 200);
          }}
          className="flex-1 px-2 py-1 text-base outline-none bg-transparent min-w-0 text-foreground placeholder:text-muted-foreground"
        />
        {(searchQuery || hasActiveFilters) && (
          <button onClick={onResetFilters} className="p-2 hover:bg-muted rounded-full flex-shrink-0">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        )}

        {/* Locate Me Button */}
        <button
          onClick={onLocateUser}
          className="p-2 hover:bg-muted rounded-full flex-shrink-0"
          title="Find my location"
        >
          <Locate className="w-5 h-5 text-primary" />
        </button>
      </div>

      {/* Search Dropdown */}
      {searchFocused && visibleBusinesses.length > 0 && (
        <div className="mt-2 bg-background rounded-xl shadow-lg border border-border max-h-64 overflow-y-auto">
          {visibleBusinesses.slice(0, 8).map(b => (
            <button
              key={b.id}
              className="w-full px-4 py-3 text-left hover:bg-accent flex items-center gap-3 border-b last:border-b-0"
              onClick={() => {
                if (blurTimeout.current) clearTimeout(blurTimeout.current);
                onSelectBusiness(b);
                setSearchFocused(false);
                setSearchQuery('');
              }}
            >
              <div className="flex-shrink-0">
                <SourceLogo business={b} size="sm" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate text-foreground">{b.business_name}</div>
                <div className="text-xs text-muted-foreground truncate">{getDisplayRegion(b)}</div>
              </div>
            </button>
          ))}
          {visibleBusinesses.length > 8 && (
            <div className="px-4 py-2 text-xs text-center text-muted-foreground bg-muted/50">
              +{visibleBusinesses.length - 8} more in area
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default SearchBar;
